// Sidebar Layout Component
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { QK } from '../../lib/queryKeys';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  Contact,
  Building2,
  Bookmark,
  CalendarDays,
  FileText,
  Settings,
  LogOut,
  ChevronDown,
  ShoppingCart,
  Gavel,
  DollarSign,
  HelpCircle,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Tag,
  MessageCircle,
  Send,
  X,
  Bell,
  CheckCheck,
  Warehouse,
  CircleStar,
} from 'lucide-react';
import OnboardingChecklist from '../onboarding/OnboardingChecklist';
import OnboardingBeacon from '../onboarding/OnboardingBeacon';
import MobileNavBar from './MobileNavBar';
import WhatsNewBanner from '../common/WhatsNewModal';

// Horse icon SVG (Updated - Pure outline)
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4 L6 10 C6 14.4 8.7 18 12 18 C15.3 18 18 14.4 18 10 L18 4" />
  </svg>
);

const SidebarLayout = ({ children, activeOverride = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, isSystemOwner, isTenantAdmin, hasFinancialAccess, tenantLogo, isFarm, isSyndicator } = useAuth();
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgConvos, setMsgConvos] = useState([]);
  const [msgActive, setMsgActive] = useState(null);
  const [msgMessages, setMsgMessages] = useState([]);
  const [msgText, setMsgText] = useState('');

  // ── Jitter helper ─────────────────────────────────────────────────────────
  // Returns false when the tab is hidden (pauses polling) or a randomised ms
  // value when active — preventing mechanical fixed-interval bot signatures.
  const jitteredInterval = (baseMs, jitterMs) => () =>
    document.hidden ? false : baseMs + Math.floor(Math.random() * jitterMs);

  // ── Message unread count ──────────────────────────────────────────────────
  // Active tab: polls every 45–60 s with jitter.  Hidden tab: paused.
  const { data: msgUnreadData } = useQuery({
    queryKey: QK.sidebarMsgUnread(),
    queryFn: () => api.get('/messages/unread-count').then(r => r.data),
    refetchInterval: jitteredInterval(45_000, 15_000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
  });
  const msgUnread = msgUnreadData?.unread_count ?? 0;

  // ── Pending tasks count ───────────────────────────────────────────────────
  // Active tab: polls every 90–120 s with jitter.  Hidden tab: paused.
  // Shared queryKey with NotificationBadge → single deduplicated request.
  const { data: tasksData } = useQuery({
    queryKey: QK.sidebarTaskCount(),
    queryFn: () => api.get('/activities/tasks/my/count').then(r => r.data),
    refetchInterval: jitteredInterval(90_000, 30_000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 60_000,
  });
  const pendingTasksCount = tasksData?.count ?? 0;

  // ── Notifications (nudges + sale alerts) ──────────────────────────────────
  // Active tab: polls every 90–120 s with jitter.  Hidden tab: paused.
  const [notifOpen, setNotifOpen] = useState(false);
  const { data: notifData, isFetching: notifLoading } = useQuery({
    queryKey: QK.sidebarNotifications(),
    queryFn: async () => {
      const [nudgesRes, alertsRes] = await Promise.all([
        api.get('/nudges/my'),
        api.get('/tenant-alert-prefs/notifications').catch(() => ({ data: [] })),
      ]);
      return { nudges: nudgesRes.data || [], alerts: alertsRes.data || [] };
    },
    refetchInterval: jitteredInterval(90_000, 30_000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 60_000,
  });
  const nudges    = notifData?.nudges  ?? [];
  const saleAlerts = notifData?.alerts ?? [];

  const notifUnread = [
    ...nudges.filter(n => !n.is_read),
    ...saleAlerts.filter(a => !a.read),
  ].length;

  const markAllRead = async () => {
    try {
      await Promise.all([
        api.post('/tenant-alert-prefs/notifications/read-all').catch(() => {}),
        ...nudges.filter(n => !n.is_read).map(n => api.put(`/nudges/${n.id}/mark-read`).catch(() => {})),
      ]);
      // Optimistic update — no extra network request needed
      queryClient.setQueryData(QK.sidebarNotifications(), prev =>
        prev ? {
          nudges:  prev.nudges.map(n => ({ ...n, is_read: true })),
          alerts:  prev.alerts.map(a => ({ ...a, read: true })),
        } : prev
      );
    } catch { /* ignore */ }
  };
  
  // Initialize from localStorage, defaulting to true (collapsed)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === null ? true : saved === 'true';
  });

  // Persist state changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);

  // Open messages panel from mobile nav
  useEffect(() => {
    const handler = () => openMsgPanel();
    window.addEventListener('open-messages', handler);
    return () => window.removeEventListener('open-messages', handler);
  }, []);

  const openMsgPanel = async () => {
    setMsgOpen(true);
    try {
      const res = await api.get('/messages/conversations');
      setMsgConvos(res.data || []);
    } catch { /* ignore */ }
  };

  const openMsgConvo = async (convoId) => {
    try {
      const res = await api.get(`/messages/conversations/${convoId}`);
      setMsgActive(res.data.conversation);
      setMsgMessages(res.data.messages || []);
      const unread = await api.get('/messages/unread-count');
      queryClient.setQueryData(QK.sidebarMsgUnread(), unread.data);
    } catch { /* ignore */ }
  };

  const sendMsg = async () => {
    if (!msgText.trim() || !msgActive) return;
    try {
      const res = await api.post('/messages/send', { conversation_id: msgActive.id, text: msgText.trim() });
      setMsgMessages(prev => [...prev, res.data]);
      setMsgText('');
      const convosRes = await api.get('/messages/conversations');
      setMsgConvos(convosRes.data || []);
    } catch { /* ignore */ }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  const isRestrictedRole = user?.roles?.some(r => ['tenant_card_runner', 'tenant_sale_clerk'].includes(r));
  const isFarmMgr = user?.roles?.includes('tenant_farm_manager');

  // Navigation items based on role
  const navItems = [
    {
      label: 'Virtual Rolodex',
      icon: null,
      isSection: true,
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'My Syndicates',
      icon: CircleStar,
      href: '/syndicates',
      show: !isRestrictedRole && !isFarmMgr && isSyndicator,
    },
    {
      label: 'Accounts',
      icon: Building2,
      href: '/crm/accounts',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Contacts',
      icon: Contact,
      href: '/crm/contacts',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Horses',
      icon: HorseIcon,
      href: '/horses',
      show: !isRestrictedRole,
    },
    {
      label: 'FarmFolio',
      icon: Warehouse,
      href: '/farmfolio',
      show: !isRestrictedRole && isFarm,
    },
    {
      label: 'Sales',
      icon: null,
      isSection: true,
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Sale Opportunities',
      icon: Tag,
      href: '/sales/opportunities',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Active Sales',
      icon: Gavel,
      href: '/sales',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Financials',
      icon: null,
      isSection: true,
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Invoices',
      icon: DollarSign,
      href: '/invoices',
      show: !isRestrictedRole && !isFarmMgr && hasFinancialAccess(),
    },
    {
      label: 'Activity',
      icon: null,
      isSection: true,
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Calendar',
      icon: CalendarDays,
      href: '/calendar',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Tasks',
      icon: FileText,
      href: '/tasks',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Reports',
      icon: BarChart3,
      href: '/reports',
      show: !isRestrictedRole && !isFarmMgr,
    },
    {
      label: 'Admin',
      icon: null,
      isSection: true,
      show: isTenantAdmin(),
    },
    {
      label: 'Settings',
      icon: Settings,
      href: '/tenant-admin',
      show: isTenantAdmin(),
    },
  ];

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside 
        className={`bg-secondary border-r border-border flex flex-col transition-[width,padding] duration-200 relative ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-6 h-6 w-6 rounded-full border border-border bg-background shadow-md z-10 hover:bg-muted"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </Button>

        {/* Logo */}
        <div className={`border-b border-border flex justify-center transition-[width,padding] duration-200 ${isCollapsed ? 'p-2' : 'p-4'}`}>
          <Link to={isRestrictedRole ? '/sales' : isFarmMgr ? '/horses' : '/dashboard'} className="flex items-center justify-center w-full">
            <img 
              src={tenantLogo || "/logo.svg"}
              alt="Green Door" 
              className={`object-contain transition-[width,padding] duration-200 ${
                isCollapsed ? 'w-10 h-10' : 'w-32 h-32'
              }`} 
            />
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4 overflow-x-hidden">
          <nav className="px-2 space-y-1">
            {navItems
              .filter((item) => item.show)
              .map((item, index) => {
                if (item.isSection) {
                  return !isCollapsed && (
                    <div key={index} className="pt-4 pb-2 px-3 animate-fade-in">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                        {item.label}
                      </span>
                    </div>
                  );
                }

                const Icon = item.icon;
                
                // Special handling for /sales vs /sales/opportunities
                let isActive;
                if (item.href === '/sales') {
                  // Active Sales should only match /sales exactly, not /sales/opportunities
                  isActive = activeOverride 
                    ? activeOverride === item.href
                    : location.pathname === item.href;
                } else {
                  isActive = activeOverride 
                    ? activeOverride === item.href || activeOverride.startsWith(item.href + '/')
                    : location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                }

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`sidebar-link ${isActive ? 'active' : ''} ${
                      isCollapsed ? 'justify-center px-2' : ''
                    }`}
                    title={isCollapsed ? item.label : ''}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="relative shrink-0">
                      {Icon && <Icon className="w-5 h-5" />}
                      {/* Show notification badge on Tasks */}
                      {item.label === 'Tasks' && pendingTasksCount > 0 && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-[9px]"
                        >
                          {pendingTasksCount > 99 ? '99' : pendingTasksCount}
                        </Badge>
                      )}
                    </div>
                    {!isCollapsed && (
                      <span className="truncate animate-fade-in flex items-center justify-between w-full">
                        {item.label}
                        {item.label === 'Tasks' && pendingTasksCount > 0 && (
                          <Badge variant="destructive" className="ml-auto">
                            {pendingTasksCount}
                          </Badge>
                        )}
                      </span>
                    )}
                  </Link>
                );
              })}
          </nav>
        </ScrollArea>

        {/* Messages */}
        {!isRestrictedRole && (
        <div className={`px-4 pb-2 ${isCollapsed ? 'flex justify-center' : ''}`}>
          <Button
            variant="ghost"
            className={`relative ${isCollapsed ? 'h-10 w-10 p-0 justify-center' : 'w-full justify-start gap-3 h-10'} hover:bg-primary/5`}
            onClick={() => msgOpen ? setMsgOpen(false) : openMsgPanel()}
            data-testid="app-messages-btn"
          >
            <MessageCircle className="w-5 h-5 text-primary shrink-0" />
            {!isCollapsed && <span className="text-sm">Messages</span>}
            {msgUnread > 0 && (
              <span className={`w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-medium ${isCollapsed ? 'absolute -top-1 -right-1' : 'ml-auto'}`}>{msgUnread}</span>
            )}
          </Button>
        </div>
        )}

        {/* Notifications Bell */}
        {!isRestrictedRole && (
        <div className={`px-4 pb-2 ${isCollapsed ? 'flex justify-center' : ''}`}>
          <Button
            variant="ghost"
            className={`relative ${isCollapsed ? 'h-10 w-10 p-0 justify-center' : 'w-full justify-start gap-3 h-10'} hover:bg-primary/5`}
            onClick={() => setNotifOpen(o => !o)}
            data-testid="app-notifications-btn"
          >
            <Bell className="w-5 h-5 text-primary shrink-0" />
            {!isCollapsed && <span className="text-sm">Notifications</span>}
            {notifUnread > 0 && (
              <span className={`w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-medium ${isCollapsed ? 'absolute -top-1 -right-1' : 'ml-auto'}`}>
                {notifUnread > 99 ? '99' : notifUnread}
              </span>
            )}
          </Button>
        </div>
        )}

        {/* Onboarding Checklist */}
        <OnboardingChecklist collapsed={isCollapsed} onExpandSidebar={() => setIsCollapsed(false)} />

        {/* User Menu */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full gap-3 h-auto p-2 hover:bg-primary/5 ${
                  isCollapsed ? 'justify-center' : 'justify-start'
                }`}
                data-testid="user-menu-trigger"
              >
                <OnboardingBeacon itemKey="update_password" tooltipSide="right">
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={user?.profile_photo_url} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </OnboardingBeacon>
                {!isCollapsed && (
                  <>
                    <div className="flex-1 text-left overflow-hidden animate-fade-in">
                      <p className="text-sm font-medium truncate">{user?.full_name || user?.first_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isCollapsed ? "center" : "end"} side={isCollapsed ? "right" : "top"} className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                <Users className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* What's New banner — shown once per release per user */}
        <WhatsNewBanner user={user} />
        <div className="p-6 animate-fade-in">
          {children}
        </div>
      </main>

      {/* Messages Panel */}
      {msgOpen && (
        <div className="fixed bottom-20 left-16 z-50 w-80 h-96 bg-white border rounded-lg shadow-xl flex flex-col overflow-hidden">
          <div className="p-3 border-b bg-primary text-white flex items-center justify-between">
            <p className="text-sm font-semibold">{msgActive ? 'Conversation' : 'Messages'}</p>
            <div className="flex gap-1">
              {msgActive && (
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white hover:bg-white/20" onClick={() => setMsgActive(null)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white hover:bg-white/20" onClick={() => setMsgOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {!msgActive ? (
            <div className="flex-1 overflow-y-auto">
              {msgConvos.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No messages yet</p>
              ) : (
                msgConvos.map(c => {
                  const other = c.participant_details?.find(p => p.id !== user?.id);
                  return (
                    <div key={c.id} className="p-3 cursor-pointer hover:bg-muted/50 border-b" onClick={() => openMsgConvo(c.id)}>
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-medium">{other?.name || 'Unknown'}</p>
                        {c.unread_count > 0 && <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">{c.unread_count}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{c.last_message || ''}</p>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {msgMessages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-2 rounded-lg text-sm ${m.sender_id === user?.id ? 'bg-primary text-white' : 'bg-muted'}`}>
                      <p>{m.text}</p>
                      <p className={`text-[9px] mt-0.5 ${m.sender_id === user?.id ? 'text-white/60' : 'text-muted-foreground'}`}>
                        {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-2 border-t flex gap-2">
                <input
                  className="flex-1 border rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Type a message..."
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendMsg(); }}
                />
                <Button size="sm" onClick={sendMsg} disabled={!msgText.trim()} className="bg-primary hover:bg-primary/90 text-white h-8 w-8 p-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {/* Notifications Panel */}
      {notifOpen && (
        <div className="fixed bottom-20 left-16 z-50 w-80 max-h-[28rem] bg-white border rounded-lg shadow-xl flex flex-col overflow-hidden">
          <div className="p-3 border-b bg-primary text-white flex items-center justify-between">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notifications
              {notifUnread > 0 && <span className="bg-red-500 text-white text-[9px] rounded-full px-1.5">{notifUnread}</span>}
            </p>
            <div className="flex gap-1">
              {notifUnread > 0 && (
                <Button variant="ghost" size="sm" className="h-6 px-1.5 text-white hover:bg-white/20 text-[10px] gap-1" onClick={markAllRead}>
                  <CheckCheck className="w-3 h-3" /> All read
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-white hover:bg-white/20" onClick={() => setNotifOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y">
            {notifLoading ? (
              <p className="text-xs text-muted-foreground text-center py-6">Loading…</p>
            ) : (nudges.length === 0 && saleAlerts.length === 0) ? (
              <p className="text-xs text-muted-foreground text-center py-6">No notifications</p>
            ) : (
              <>
                {/* Sale Alerts */}
                {saleAlerts.map(a => (
                  <div key={a.id}
                    className={`px-3 py-2.5 ${a.read ? 'opacity-60' : 'bg-blue-50/40'} ${a.link ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                    onClick={() => {
                      if (a.link) {
                        api.post(`/tenant-alert-prefs/notifications/${a.id}/read`).catch(() => {});
                        queryClient.setQueryData(QK.sidebarNotifications(), prev => prev ? { ...prev, alerts: prev.alerts.map(x => x.id === a.id ? { ...x, read: true } : x) } : prev);
                        navigate(a.link);
                        setNotifOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium leading-snug">{a.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{a.message}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</p>
                      </div>
                      {!a.read && (
                        <button className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1 shrink-0"
                          onClick={(e) => { e.stopPropagation(); api.post(`/tenant-alert-prefs/notifications/${a.id}/read`).catch(() => {}); queryClient.setQueryData(QK.sidebarNotifications(), prev => prev ? { ...prev, alerts: prev.alerts.map(x => x.id === a.id ? { ...x, read: true } : x) } : prev); }} />
                      )}
                    </div>
                  </div>
                ))}
                {/* Nudges */}
                {nudges.map(n => (
                  <div key={n.id} className={`px-3 py-2.5 cursor-pointer hover:bg-muted/30 ${n.is_read ? 'opacity-60' : 'bg-yellow-50/40'}`}
                    onClick={async () => {
                      if (!n.is_read) { await api.put(`/nudges/${n.id}/mark-read`).catch(()=>{}); queryClient.setQueryData(QK.sidebarNotifications(), prev => prev ? { ...prev, nudges: prev.nudges.map(x => x.id === n.id ? {...x, is_read: true} : x) } : prev); }
                      if (n.link) navigate(n.link);
                      else if (n.sale_opportunity_id) navigate(`/sales/opportunities/${n.sale_opportunity_id}`);
                      setNotifOpen(false);
                    }}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium leading-snug">{n.title || 'Nudge'}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{n.message || n.content}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                      </div>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1 shrink-0" />}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile bottom navigation (hidden on desktop via CSS) */}
      <MobileNavBar />
    </div>
  );
};

export default SidebarLayout;
